import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSupabaseClient } from '../../../lib/supabase';
import virtualPosService from '../../../services/virtualPosService';
import { getCharge, listCharges, retryCharge as yoolpayRetryCharge } from '../../../services/yoolpay/chargesApi';
import { refreshChargeMetadata as refreshChargeMetadataShared } from '../../../services/payments/virtualposChargeSync.js';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { ChevronDown, ChevronRight, Eye, RefreshCw, Receipt } from 'lucide-react';
import formatters from '../../../styles/tokens/formatters';
import { instanteDeDb, msDeDb } from '../../../lib/fechas';
import VirtualPosPaymentDetailDialog, { VirtualPosPaymentDetailContent } from '../../../components/payments/VirtualPosPaymentDetailDialog';
import { EmptyState } from '../../../components/ui/empty-state';

const getMetadataObject = (metadataValue) => {
  if (!metadataValue) return null;
  if (typeof metadataValue === 'string') {
    try {
      return JSON.parse(metadataValue);
    } catch (_) {
      return null;
    }
  }
  if (typeof metadataValue === 'object') return metadataValue;
  return null;
};

const parseNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const trimmed = value.replace(/\s/g, '');
  const hasDot = trimmed.includes('.');
  const hasComma = trimmed.includes(',');
  const normalized = (() => {
    if (hasDot && hasComma) {
      return trimmed.replace(/\./g, '').replace(/,/g, '.');
    }
    if (hasComma && !hasDot) {
      return trimmed.replace(/,/g, '.');
    }
    return trimmed;
  })();
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const getPaymentTrackingFromMetadata = (metadata) => {
  const chargedCLP =
    metadata?.charged_amount_clp ??
    metadata?.amount_clp ??
    metadata?.charged_clp ??
    metadata?.amount_pesos ??
    metadata?.clp_amount ??
    metadata?.payment?.amount_clp ??
    metadata?.payment?.charged_amount_clp ??
    metadata?.virtualpos_payment?.amount_clp ??
    null;

  const netCLP =
    metadata?.net_amount_clp ??
    metadata?.net_clp ??
    metadata?.recaudado_clp ??
    metadata?.payment?.net_amount_clp ??
    metadata?.virtualpos_payment?.net_amount_clp ??
    null;

  const feesCLP =
    metadata?.fees_clp ??
    metadata?.fee_clp ??
    metadata?.commissions_clp ??
    metadata?.commission_clp ??
    metadata?.payment?.fees_clp ??
    metadata?.payment?.commission_clp ??
    metadata?.virtualpos_payment?.fees_clp ??
    null;

  const depositDate =
    metadata?.deposit_date ??
    metadata?.deposit_at ??
    metadata?.deposited_at ??
    metadata?.settlement_date ??
    metadata?.settled_at ??
    metadata?.payment?.deposit_date ??
    metadata?.payment?.settlement_date ??
    metadata?.virtualpos_payment?.deposit_date ??
    null;

  const paidAt =
    metadata?.paid_at ??
    metadata?.transaction_date ??
    metadata?.processed_at ??
    metadata?.payment?.paid_at ??
    metadata?.virtualpos_payment?.paid_at ??
    null;

  const order = metadata?.charge?.payment?.order || null;
  const deposits = Array.isArray(order?.deposits) ? order.deposits : [];
  const firstDeposit = deposits?.[0] || null;

  const chargedFromOrder = parseNumber(order?.amount);
  const netFromDeposit = parseNumber(firstDeposit?.payout_amount);
  const feesFromDeposit = (() => {
    const processingFee = parseNumber(firstDeposit?.processing_fee);
    const flatFee = parseNumber(firstDeposit?.processing_flat_fee);
    if (processingFee === null && flatFee === null) return null;
    return (processingFee || 0) + (flatFee || 0);
  })();

  return {
    chargedCLP: chargedCLP ?? chargedFromOrder,
    netCLP: netCLP ?? netFromDeposit,
    feesCLP: feesCLP ?? feesFromDeposit,
    depositDate: depositDate ?? firstDeposit?.payout_date ?? null,
    paidAt: paidAt ?? order?.authorized_at ?? order?.created_at ?? null
  };
};

const formatChargeAmount = (amount, currency) => {
  if (amount === null || amount === undefined) return '-';
  if (String(currency || '').toUpperCase() === 'UF') return formatters.formatUF(amount);
  return formatters.formatCLP(amount);
};

const formatCLP = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const numericValue = typeof value === 'string' ? Number(value) : value;
  if (typeof numericValue !== 'number' || Number.isNaN(numericValue)) return '-';
  return formatters.formatCLP(numericValue);
};

const formatDate = (value) => {
  if (!value) return '-';
  return formatters.formatDate(value);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  return formatters.formatDateTime(value);
};

// Estas dos vivían acá duplicadas letra por letra desde `PaymentsList`. Ahora
// salen de `lib/fechas`, que además documenta POR QUÉ hay que normalizar: varias
// columnas de pagos son `timestamp without time zone` y `new Date()` las lee como
// hora local. Se les deja el nombre de siempre para no tocar los call sites.
const normalizeDateValue = instanteDeDb;
const getDateTimeValue = msDeDb;

const getChargeStatusBadge = (status) => {
  const raw = String(status || '').toLowerCase();
  const CHARGE_MAP = {
    'pendiente':  { variant: 'pendiente',  label: 'Pendiente' },
    'pending':    { variant: 'pendiente',  label: 'Pendiente' },
    'pagado':     { variant: 'pagado',     label: 'Pagado' },
    'paid':       { variant: 'pagado',     label: 'Pagado' },
    'cancelado':  { variant: 'cancelado',  label: 'Cancelado' },
    'cancelled':  { variant: 'cancelado',  label: 'Cancelado' },
    'procesando': { variant: 'procesando', label: 'Procesando' },
    'processing': { variant: 'procesando', label: 'Procesando' },
    'rechazado':  { variant: 'expirado',   label: 'Rechazado' },
    'rejected':   { variant: 'expirado',   label: 'Rechazado' },
    'failed':     { variant: 'expirado',   label: 'Fallido' },
  };
  const cfg = CHARGE_MAP[raw] || { variant: 'outline', label: status || '—' };
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
};

const normalizeChargeStatus = (status) => {
  const rawStatus = String(status || '').toLowerCase();
  const map = {
    pendiente: 'pending',
    pagado: 'paid',
    cancelado: 'cancelled',
    procesando: 'processing',
    rechazado: 'rejected',
    failed: 'rejected',
  };
  return map[rawStatus] || rawStatus;
};

const getQuotaDescription = (row) => {
  const paymentMetadata = getMetadataObject(row?.payment_metadata);
  const providerMetadata = getMetadataObject(row?.provider_metadata);
  const metadata = row?.payment_metadata ? paymentMetadata : providerMetadata;
  return row?.description
    || metadata?.charge?.description
    || metadata?.charge?.payment?.order?.description
    || '-';
};

const buildChargeDetailPayload = (row) => {
  const paymentMetadata = getMetadataObject(row?.payment_metadata);
  const providerMetadata = getMetadataObject(row?.provider_metadata);
  const metadata = row?.payment_metadata ? paymentMetadata : providerMetadata;
  const metadataText = row?.payment_metadata
    ? paymentMetadata
      ? JSON.stringify(paymentMetadata, null, 2)
      : String(row?.payment_metadata || '')
    : providerMetadata
      ? JSON.stringify(providerMetadata, null, 2)
      : '';
  const tracking = getPaymentTrackingFromMetadata(metadata);
  const virtualposCharge = metadata?.charge || null;
  const virtualposPayment = virtualposCharge?.payment || null;
  const virtualposOrder = virtualposPayment?.order || null;
  const virtualposDeposits = Array.isArray(virtualposOrder?.deposits) ? virtualposOrder.deposits : [];
  const virtualposClient = virtualposPayment?.client || null;
  const virtualposCommerce = virtualposPayment?.commerce || null;
  const chargeAmount = parseNumber(virtualposCharge?.amount);
  const orderAmount = parseNumber(virtualposOrder?.amount);

  const formattedDeposits = virtualposDeposits.map((deposit) => ({
    description: deposit?.description || '-',
    payout_date: deposit?.payout_date ? formatDate(deposit.payout_date) : '-',
    payout_amount: formatCLP(parseNumber(deposit?.payout_amount) ?? deposit?.payout_amount),
    processing_fee: formatCLP(parseNumber(deposit?.processing_fee) ?? deposit?.processing_fee),
    processing_flat_fee: formatCLP(parseNumber(deposit?.processing_flat_fee) ?? deposit?.processing_flat_fee),
  }));

  const identifiers = (virtualposOrder?.auth_code || virtualposOrder?.payment_type_code || virtualposOrder?.uuid || virtualposCharge?.internal_code)
    ? [
      { label: 'Auth code:', value: virtualposOrder?.auth_code, mono: true },
      { label: 'Tipo de pago:', value: virtualposOrder?.payment_type_code, mono: true },
      { label: 'Order UUID:', value: virtualposOrder?.uuid, mono: true },
      { label: 'Internal code:', value: virtualposCharge?.internal_code, mono: true },
    ]
    : undefined;

  const collectionItems = (tracking.chargedCLP || tracking.netCLP)
    ? [
      { label: 'Monto Cobrado (CLP):', value: formatCLP(tracking.chargedCLP || 0) },
      { label: 'Comisión Pasarela:', value: `-${formatCLP(tracking.feesCLP || 0)}`, className: 'font-medium text-[var(--yool-error)]' },
      { label: 'Neto Recaudado:', value: formatCLP(tracking.netCLP || 0), boldLabel: true, className: 'font-bold text-green-700' },
      { label: 'Depósito:', value: tracking.depositDate ? formatDateTime(tracking.depositDate) : '-' },
      {
        label: 'Procesado:',
        value: row?.processed_at
          ? formatDateTime(row.processed_at)
          : tracking.paidAt
            ? formatDateTime(tracking.paidAt)
            : '-',
      },
    ]
    : undefined;

  const chargeItems = virtualposCharge
    ? [
      { label: 'Charge ID:', value: virtualposCharge?.id || '-', mono: true },
      { label: 'Estado Charge:', value: getChargeStatusBadge(virtualposCharge?.status || '-') },
      {
        label: 'Monto Charge:',
        value: (
          <span className="font-medium">
            {chargeAmount !== null ? formatChargeAmount(chargeAmount, row?.currency) : virtualposCharge?.amount ?? '-'} {row?.currency || ''}
          </span>
        ),
      },
      { label: 'Fecha Charge:', value: virtualposCharge?.charge_date ? formatDate(virtualposCharge.charge_date) : '-' },
      { label: 'Descripción:', value: virtualposCharge?.description || '-', className: 'break-words' },
    ]
    : undefined;

  const orderItems = virtualposOrder
    ? [
      { label: 'Orden UUID:', value: virtualposOrder?.uuid || '-', mono: true },
      { label: 'Estado Orden:', value: getChargeStatusBadge(virtualposOrder?.status || '-') },
      { label: 'Monto Orden (CLP):', value: orderAmount !== null ? formatCLP(orderAmount) : virtualposOrder?.amount ?? '-' },
      { label: 'Authorized At:', value: virtualposOrder?.authorized_at ? formatDateTime(virtualposOrder.authorized_at) : '-' },
      { label: 'Created At:', value: virtualposOrder?.created_at ? formatDateTime(virtualposOrder.created_at) : '-' },
      { label: 'Installment Number:', value: virtualposOrder?.installment_number ?? '-', mono: true },
      { label: 'Installment Amount:', value: virtualposOrder?.installment_amount ?? '-', mono: true },
      { label: 'Card Number:', value: virtualposOrder?.card_number ?? '-', mono: true },
    ]
    : undefined;

  const metadataExtraItems = [
    { label: 'Code:', value: metadata?.code ?? '-', mono: true },
    { label: 'Status (JSON):', value: metadata?.status ?? '-', mono: true },
    { label: 'External Charge ID:', value: row?.external_charge_id || '-', mono: true },
    { label: 'Compañía:', value: row?.compania_nombre || '-', className: 'break-words' },
    { label: 'Plan:', value: row?.plan_name || '-', className: 'break-words' },
  ];

  return {
    metadata,
    tracking,
    summary: {
      orderId: virtualposOrder?.uuid || '-',
      amount: formatChargeAmount(chargeAmount ?? row?.amount, row?.currency),
      status: getChargeStatusBadge(virtualposCharge?.status || row?.status || '-'),
      date: virtualposCharge?.charge_date ? formatDate(virtualposCharge.charge_date) : formatDate(row?.charge_date),
    },
    identifiers,
    customerItems: [
      {
        label: 'Cliente:',
        value: virtualposClient
          ? `${virtualposClient?.first_name || ''} ${virtualposClient?.last_name || ''}`.trim() || '-'
          : '-',
      },
      { label: 'Comercio (VirtualPOS):', value: virtualposCommerce?.name || '-' },
      { label: 'RUT:', value: virtualposClient?.social_id || '-', mono: true },
      { label: 'Email:', value: virtualposClient?.email || '-', className: 'break-all' },
      { label: 'Teléfono:', value: virtualposClient?.phone || '-', mono: true },
      { label: 'Suscripción ID:', value: row?.subscription_id || '-', mono: true },
    ],
    collectionItems,
    chargeItems,
    orderItems,
    deposits: formattedDeposits,
    metadataText,
    metadataExtraItems,
  };
};

const isUuidValue = (value) => /^[0-9a-fA-F-]{36}$/.test(String(value || ''));

const getRetryDisabledReason = (row) => {
  if (!row?.external_charge_id) return 'Cobro no disponible: falta External Charge ID.';
  const normalizedStatus = normalizeChargeStatus(row?.status);
  if (normalizedStatus === 'paid') return 'Cobro no disponible: cargo pagado.';
  if (normalizedStatus === 'processing') return 'Cobro no disponible: cargo en procesamiento.';
  if (normalizedStatus === 'cancelled') return 'Cobro no disponible: cargo cancelado.';
  return '';
};

const isRetryDisabled = (row) => Boolean(getRetryDisabledReason(row));

const SubscriptionChargesList = () => {
  const supabase = useSupabaseClient();
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [suscripcionesById, setSuscripcionesById] = useState({});
  const [contratacionesBySubscriptionId, setContratacionesBySubscriptionId] = useState({});
  const [certificadosBySubscriptionId, setCertificadosBySubscriptionId] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [chargingChargeId, setChargingChargeId] = useState(null);
  const [refreshingChargeId, setRefreshingChargeId] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [actionFeedback, setActionFeedback] = useState(null);
  const [itemsDialog, setItemsDialog] = useState(null);
  const navigate = useNavigate();

  const getLinkedRecords = (row) => {
    const subscriptionId = row?.subscription_id ? String(row.subscription_id) : null;
    const suscripcion = subscriptionId ? suscripcionesById?.[subscriptionId] : null;
    const contrataciones = (subscriptionId && contratacionesBySubscriptionId?.[subscriptionId]) || [];
    const certificados = (subscriptionId && certificadosBySubscriptionId?.[subscriptionId]) || [];
    return { suscripcion, contrataciones, certificados };
  };

  const getSubscriberInfo = (row) => {
    const { contrataciones, certificados } = getLinkedRecords(row);
    const contratacion = contrataciones?.[0] || null;
    const certificado = certificados?.[0] || null;
    const paymentMetadata = getMetadataObject(row?.payment_metadata);
    const providerMetadata = getMetadataObject(row?.provider_metadata);
    const metadata = row?.payment_metadata ? paymentMetadata : providerMetadata;
    const virtualposClient = metadata?.charge?.payment?.client || null;
    const name = contratacion?.nombre
      || certificado?.cliente_nombre
      || (virtualposClient ? `${virtualposClient?.first_name || ''} ${virtualposClient?.last_name || ''}`.trim() : '');
    const rut = contratacion?.rut_titular
      || contratacion?.rut
      || certificado?.cliente_rut
      || virtualposClient?.social_id
      || '';
    return { name, rut };
  };

  const loadCharges = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      // Los cargos salen por payment, no por Supabase: `subscription_charges` es la única
      // tabla de esta pantalla sin policy de lectura general, y el token de Clerk no trae
      // con qué reconocer al staff en el RLS (ver services/yoolpay/chargesApi.js). Las
      // otras tres vistas de acá abajo sí se leen directo, que para ellas sí funciona.
      let data = [];
      let error = null;
      try {
        data = await listCharges();
      } catch (err) {
        error = err;
      }

      if (error) {
        console.error('Error loading charges from payment:', error);
        setLoadError('Error al cargar los cargos desde el portal de pagos.');
        setCharges([]);
        setSuscripcionesById({});
        setContratacionesBySubscriptionId({});
        setCertificadosBySubscriptionId({});
      } else {
        const rows = Array.isArray(data) ? data : [];
        setCharges(rows);

        const subscriptionIds = Array.from(
          new Set(
            rows
              .map((row) => row?.subscription_id)
              .filter(Boolean)
              .map((value) => String(value))
          )
        );

        if (subscriptionIds.length === 0) {
          setSuscripcionesById({});
          setContratacionesBySubscriptionId({});
          setCertificadosBySubscriptionId({});
        } else {
          const SUSCRIPCION_SELECT = 'id,subscription_id,codigo,external_subscription_id,certificado_id,plan_name,virtualpos_plan_id,company_id,compania_nombre,compania_rut,status,contrataciones_data';
          const [
            { data: suscripcionesBySubData, error: suscripcionesBySubError },
            { data: suscripcionesByIdData, error: suscripcionesByIdError },
          ] = await Promise.all([
            supabase
              .from('company_suscripciones')
              .select(SUSCRIPCION_SELECT)
              .in('subscription_id', subscriptionIds),
            supabase
              .from('company_suscripciones')
              .select(SUSCRIPCION_SELECT)
              .in('id', subscriptionIds),
          ]);

          let suscripcionesMap = {};
          const suscripcionesError = suscripcionesBySubError || suscripcionesByIdError;
          if (suscripcionesError) {
            console.error('Error loading company_suscripciones for charges:', suscripcionesError);
            setSuscripcionesById({});
          } else {
            (suscripcionesByIdData || []).forEach((row) => {
              if (row?.id) suscripcionesMap[String(row.id)] = row;
            });
            (suscripcionesBySubData || []).forEach((row) => {
              if (row?.subscription_id) suscripcionesMap[String(row.subscription_id)] = row;
            });
            setSuscripcionesById(suscripcionesMap);
          }

          const contratacionIds = new Set();
          const certificadoIds = new Set();
          const legacySubscriptionIds = new Set(subscriptionIds);
          Object.values(suscripcionesMap).forEach((suscripcion) => {
            if (suscripcion?.subscription_id) legacySubscriptionIds.add(String(suscripcion.subscription_id));
            if (suscripcion?.id) legacySubscriptionIds.add(String(suscripcion.id));
            const contrData = getMetadataObject(suscripcion?.contrataciones_data);
            if (Array.isArray(contrData)) {
              contrData.forEach((entry) => {
                if (entry?.contratacion_id) contratacionIds.add(String(entry.contratacion_id));
                if (entry?.certificado_id) certificadoIds.add(String(entry.certificado_id));
              });
            }
          });
          const legacySubscriptionIdList = Array.from(legacySubscriptionIds).filter(isUuidValue);
          const contratacionIdList = Array.from(contratacionIds).filter(isUuidValue);
          const certificadoIdList = Array.from(certificadoIds).filter(isUuidValue);

          // Map any known subscription identifier (uuid id or short subscription_id/code)
          // back to the key(s) used by company_subscription_charges.subscription_id,
          // so legacy rows looked up by either form still resolve in getLinkedRecords.
          const identifierToChargeKeys = new Map();
          const registerIdentifier = (identifier, chargeKey) => {
            if (!identifier || !chargeKey) return;
            const idKey = String(identifier);
            if (!identifierToChargeKeys.has(idKey)) identifierToChargeKeys.set(idKey, new Set());
            identifierToChargeKeys.get(idKey).add(String(chargeKey));
          };
          subscriptionIds.forEach((chargeKey) => {
            registerIdentifier(chargeKey, chargeKey);
            const suscripcion = suscripcionesMap[chargeKey];
            if (suscripcion?.id) registerIdentifier(suscripcion.id, chargeKey);
            if (suscripcion?.subscription_id) registerIdentifier(suscripcion.subscription_id, chargeKey);
          });
          const resolveChargeKeys = (identifier) => {
            if (!identifier) return [];
            const keys = identifierToChargeKeys.get(String(identifier));
            return keys ? Array.from(keys) : [String(identifier)];
          };

          const [
            { data: contratacionesByIdData, error: contratacionesByIdError },
            { data: contratacionesBySubData, error: contratacionesBySubError },
            { data: certificadosByIdData, error: certificadosByIdError },
            { data: certificadosBySubData, error: certificadosBySubError },
            { data: certificadosByContratacionData, error: certificadosByContratacionError },
          ] = await Promise.all([
            contratacionIdList.length > 0
              ? supabase
                .from('company_contrataciones')
                .select('id,subscription_id,producto_nombre,plan_nombre,nombre,rut,rut_titular')
                .in('id', contratacionIdList)
              : Promise.resolve({ data: [], error: null }),
            supabase
              .from('company_contrataciones')
              .select('id,subscription_id,producto_nombre,plan_nombre,nombre,rut,rut_titular')
              .in('subscription_id', legacySubscriptionIdList),
            certificadoIdList.length > 0
              ? supabase
                .from('company_certificados')
                .select('id,subscription_id,codigo,cliente_nombre,cliente_rut,plan_nombre,producto_nombre')
                .in('id', certificadoIdList)
              : Promise.resolve({ data: [], error: null }),
            supabase
              .from('company_certificados')
              .select('id,subscription_id,codigo,cliente_nombre,cliente_rut,plan_nombre,producto_nombre')
              .in('subscription_id', legacySubscriptionIdList),
            contratacionIdList.length > 0
              ? supabase
                .from('company_certificados')
                .select('id,subscription_id,contratacion_id,codigo,cliente_nombre,cliente_rut,plan_nombre,producto_nombre')
                .in('contratacion_id', contratacionIdList)
              : Promise.resolve({ data: [], error: null }),
          ]);

          const contratacionesError = contratacionesByIdError || contratacionesBySubError;
          if (contratacionesError) {
            console.error('Error loading company_contrataciones for charges:', contratacionesError);
            setContratacionesBySubscriptionId({});
          } else {
            const byId = new Map();
            [...(contratacionesByIdData || []), ...(contratacionesBySubData || [])].forEach((row) => {
              if (row?.id) byId.set(String(row.id), row);
            });

            const nextMap = {};
            const addToMap = (subscriptionId, contratacion) => {
              if (!subscriptionId || !contratacion) return;
              const key = String(subscriptionId);
              if (!nextMap[key]) nextMap[key] = [];
              if (nextMap[key].some((item) => String(item?.id) === String(contratacion.id))) return;
              nextMap[key].push(contratacion);
            };

            subscriptionIds.forEach((subscriptionId) => {
              const suscripcion = suscripcionesMap[subscriptionId];
              const contrData = getMetadataObject(suscripcion?.contrataciones_data);
              if (Array.isArray(contrData) && contrData.length > 0) {
                contrData.forEach((entry) => {
                  const contratacion = entry?.contratacion_id ? byId.get(String(entry.contratacion_id)) : null;
                  if (contratacion) addToMap(subscriptionId, contratacion);
                });
              }
            });
            (contratacionesBySubData || []).forEach((row) => {
              resolveChargeKeys(row?.subscription_id).forEach((chargeKey) => addToMap(chargeKey, row));
            });
            setContratacionesBySubscriptionId(nextMap);
          }

          const certificadosError = certificadosByIdError || certificadosBySubError || certificadosByContratacionError;
          if (certificadosError) {
            console.error('Error loading company_certificados for charges:', certificadosError);
            setCertificadosBySubscriptionId({});
          } else {
            const byId = new Map();
            const byContratacionId = new Map();
            [
              ...(certificadosByIdData || []),
              ...(certificadosBySubData || []),
              ...(certificadosByContratacionData || []),
            ].forEach((row) => {
              if (row?.id) byId.set(String(row.id), row);
              if (row?.contratacion_id) byContratacionId.set(String(row.contratacion_id), row);
            });

            const nextMap = {};
            const addToMap = (subscriptionId, certificado) => {
              if (!subscriptionId || !certificado) return;
              const key = String(subscriptionId);
              if (!nextMap[key]) nextMap[key] = [];
              if (nextMap[key].some((item) => String(item?.id) === String(certificado.id))) return;
              nextMap[key].push(certificado);
            };

            subscriptionIds.forEach((subscriptionId) => {
              const suscripcion = suscripcionesMap[subscriptionId];
              const contrData = getMetadataObject(suscripcion?.contrataciones_data);
              if (Array.isArray(contrData) && contrData.length > 0) {
                contrData.forEach((entry) => {
                  const certificado = (entry?.certificado_id ? byId.get(String(entry.certificado_id)) : null)
                    || (entry?.contratacion_id ? byContratacionId.get(String(entry.contratacion_id)) : null);
                  if (certificado) addToMap(subscriptionId, certificado);
                });
              }
            });
            (certificadosBySubData || []).forEach((row) => {
              resolveChargeKeys(row?.subscription_id).forEach((chargeKey) => addToMap(chargeKey, row));
            });
            setCertificadosBySubscriptionId(nextMap);
          }
        }
      }
    } catch (err) {
      console.error('Unexpected error loading charges:', err);
      setLoadError('Error inesperado al cargar los cargos.');
      setCharges([]);
      setSuscripcionesById({});
      setContratacionesBySubscriptionId({});
      setCertificadosBySubscriptionId({});
    } finally {
      setLoading(false);
    }
  };

  // `updatePayload` trae además las columnas que el refresco promueve desde la
  // metadata (`amount_paid`, `processed_at`). Sin ellas la fila seguía mostrando
  // el cobro sin monto hasta recargar, justo en la pantalla donde se aprieta el
  // botón.
  const applyChargeMetadataUpdate = (dbChargeId, newMetadata, derivedStatus, updatePayload) => {
    if (!dbChargeId || !newMetadata) return;
    setCharges((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((item) => {
        if (String(item?.id || '') !== String(dbChargeId)) return item;
        return {
          ...item,
          payment_metadata: newMetadata,
          ...(derivedStatus ? { status: derivedStatus } : {}),
          ...(updatePayload?.amount_paid !== undefined
            ? { amount_paid: updatePayload.amount_paid }
            : {}),
          ...(updatePayload?.processed_at
            ? { processed_at: updatePayload.processed_at }
            : {})
        };
      });
    });
  };

  const handleRetryCharge = async (row) => {
    const externalChargeId = row?.external_charge_id ? String(row.external_charge_id) : '';
    const dbChargeId = row?.id ? String(row.id) : '';
    if (!externalChargeId || !dbChargeId) {
      setActionFeedback({ type: 'error', message: 'No se puede cobrar: falta External Charge ID o ID interno.' });
      return;
    }

    try {
      setChargingChargeId(externalChargeId);
      setActionFeedback(null);
      await yoolpayRetryCharge(dbChargeId);
      const { metadata: newMetadata, derivedStatus, updatePayload } = await refreshChargeMetadataShared({
        getChargeById: virtualPosService.getChargeById.bind(virtualPosService),
        getPaymentById: virtualPosService.getPaymentById.bind(virtualPosService),
        supabase,
        dbChargeId,
        externalChargeId,
        existingMetadata: row?.payment_metadata || null,
        // Para no pisar un `processed_at` ya registrado al promover el cobro.
        existingCharge: row || null,
      });
      applyChargeMetadataUpdate(dbChargeId, newMetadata, derivedStatus, updatePayload);
      setActionFeedback({ type: 'success', message: 'Cobro reintentado y metadata actualizada.' });
    } catch (error) {
      console.error('Error reintentando cobro:', error);
      setActionFeedback({ type: 'error', message: error?.message || 'Error al reintentar el cobro.' });
    } finally {
      setChargingChargeId(null);
    }
  };

  const handleRefreshChargeMetadata = async (row) => {
    const externalChargeId = row?.external_charge_id ? String(row.external_charge_id) : '';
    const dbChargeId = row?.id ? String(row.id) : '';
    if (!externalChargeId || !dbChargeId) {
      setActionFeedback({ type: 'error', message: 'No se puede actualizar: falta External Charge ID o ID interno.' });
      return;
    }

    try {
      setRefreshingChargeId(externalChargeId);
      setActionFeedback(null);
      const { metadata: newMetadata, derivedStatus, updatePayload } = await refreshChargeMetadataShared({
        getChargeById: virtualPosService.getChargeById.bind(virtualPosService),
        getPaymentById: virtualPosService.getPaymentById.bind(virtualPosService),
        supabase,
        dbChargeId,
        externalChargeId,
        existingMetadata: row?.payment_metadata || null,
        // Para no pisar un `processed_at` ya registrado al promover el cobro.
        existingCharge: row || null,
      });
      applyChargeMetadataUpdate(dbChargeId, newMetadata, derivedStatus, updatePayload);
      setActionFeedback({ type: 'success', message: 'Metadata del cargo actualizada.' });
    } catch (error) {
      console.error('Error actualizando metadata del cargo:', error);
      setActionFeedback({ type: 'error', message: error?.message || 'Error al actualizar la metadata del cargo.' });
    } finally {
      setRefreshingChargeId(null);
    }
  };

  const handleOpenChargePage = (row) => {
    const detailId = row?.id || row?.external_charge_id;
    if (!detailId) return;
    navigate(`/payments-admin/cargos/${detailId}`);
  };

  useEffect(() => {
    loadCharges();
  }, []);

  const filteredCharges = useMemo(() => {
    const normalizedFilter = normalizeChargeStatus(statusFilter);
    const filteredByStatus = statusFilter === 'all'
      ? [...charges]
      : charges.filter((row) => normalizeChargeStatus(row?.status) === normalizedFilter);
    const trimmedSearch = String(searchTerm || '').trim().toLowerCase();
    const filtered = trimmedSearch
      ? filteredByStatus.filter((row) => {
        const { name, rut } = getSubscriberInfo(row);
        const subscriptionId = row?.subscription_id ? String(row.subscription_id) : null;
        const suscripcion = subscriptionId ? suscripcionesById?.[subscriptionId] : null;
        const externalSubscriptionId = suscripcion?.external_subscription_id || '';
        const haystack = `${name || ''} ${rut || ''} ${externalSubscriptionId}`.trim().toLowerCase();
        return haystack.includes(trimmedSearch);
      })
      : filteredByStatus;

    filtered.sort((a, b) => {
      const nowTime = Date.now();
      const aTime = getDateTimeValue(a?.processed_at) ?? getDateTimeValue(a?.charge_date) ?? getDateTimeValue(a?.created_at);
      const bTime = getDateTimeValue(b?.processed_at) ?? getDateTimeValue(b?.charge_date) ?? getDateTimeValue(b?.created_at);
      if (aTime === null && bTime === null) return 0;
      if (aTime === null) return 1;
      if (bTime === null) return -1;
      const aIsFuture = aTime > nowTime;
      const bIsFuture = bTime > nowTime;
      if (aIsFuture !== bIsFuture) return aIsFuture ? 1 : -1;
      if (aIsFuture) return aTime - bTime;
      return bTime - aTime;
    });

    return filtered;
  }, [charges, statusFilter, searchTerm, contratacionesBySubscriptionId, certificadosBySubscriptionId, suscripcionesById]);

  const groupedCharges = useMemo(() => {
    const groups = [];
    const groupMap = new Map();

    filteredCharges.forEach((row, index) => {
      const subscriptionId = row?.subscription_id ? String(row.subscription_id) : null;
      const suscripcion = subscriptionId ? suscripcionesById?.[subscriptionId] : null;
      const codigo = suscripcion?.codigo || null;
      const fallbackKey = row?.id
        ? `row-${row.id}`
        : row?.external_charge_id
          ? `external-${row.external_charge_id}`
          : `row-${index}`;
      const groupKey = codigo ? `codigo-${codigo}` : subscriptionId ? `subscription-${subscriptionId}` : fallbackKey;

      if (!groupMap.has(groupKey)) {
        const group = {
          key: groupKey,
          codigo,
          subscriptionId,
          suscripcion,
          rows: [],
        };
        groupMap.set(groupKey, group);
        groups.push(group);
      }

      groupMap.get(groupKey).rows.push(row);
    });

    groups.forEach((group) => {
      group.rows.sort((a, b) => {
        const aTime = getDateTimeValue(a?.charge_date) ?? getDateTimeValue(a?.created_at);
        const bTime = getDateTimeValue(b?.charge_date) ?? getDateTimeValue(b?.created_at);
        if (aTime === null && bTime === null) return 0;
        if (aTime === null) return 1;
        if (bTime === null) return -1;
        return aTime - bTime;
      });
    });

    groups.sort((a, b) => {
      const aTime = a.rows.reduce((max, row) => {
        const t = getDateTimeValue(row?.charge_date) ?? getDateTimeValue(row?.created_at);
        return t !== null && t > max ? t : max;
      }, -Infinity);
      const bTime = b.rows.reduce((max, row) => {
        const t = getDateTimeValue(row?.charge_date) ?? getDateTimeValue(row?.created_at);
        return t !== null && t > max ? t : max;
      }, -Infinity);
      return bTime - aTime;
    });

    return groups;
  }, [filteredCharges, suscripcionesById]);

  const chargeSummary = useMemo(() => {
    const summary = {
      total: 0,
      pending: 0,
      processing: 0,
      paid: 0,
      rejected: 0,
      cancelled: 0,
      overdue: 0,
    };
    const todayEndTime = (() => {
      const now = new Date();
      now.setHours(23, 59, 59, 999);
      return now.getTime();
    })();
    filteredCharges.forEach((row) => {
      summary.total += 1;
      const normalizedStatus = normalizeChargeStatus(row?.status);
      if (normalizedStatus && Object.prototype.hasOwnProperty.call(summary, normalizedStatus)) {
        summary[normalizedStatus] += 1;
      }
      const isOverdueStatus = ['pending', 'processing', 'rejected', 'failed'].includes(normalizedStatus);
      const chargeTime = getDateTimeValue(row?.charge_date) ?? getDateTimeValue(row?.created_at);
      if (isOverdueStatus && chargeTime !== null && chargeTime <= todayEndTime) {
        summary.overdue += 1;
      }
    });
    return summary;
  }, [filteredCharges]);

  const getLatestRelevantCharge = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const todayEndTime = (() => {
      const now = new Date();
      now.setHours(23, 59, 59, 999);
      return now.getTime();
    })();
    const candidates = rows.filter((row) => {
      const dueTime = getDateTimeValue(row?.charge_date) ?? getDateTimeValue(row?.created_at);
      return dueTime !== null && dueTime <= todayEndTime;
    });
    if (candidates.length === 0) return null;
    return candidates.reduce((latest, current) => {
      const latestTime = getDateTimeValue(latest?.charge_date) ?? getDateTimeValue(latest?.created_at) ?? -Infinity;
      const currentTime = getDateTimeValue(current?.charge_date) ?? getDateTimeValue(current?.created_at) ?? -Infinity;
      return currentTime > latestTime ? current : latest;
    }, candidates[0]);
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const renderChargeDetailCell = (row) => {
    const payload = buildChargeDetailPayload(row);
    const isCharging = chargingChargeId === String(row?.external_charge_id || '');
    const isRefreshing = refreshingChargeId === String(row?.external_charge_id || '');
    const retryDisabledReason = getRetryDisabledReason(row);

    return (
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleRetryCharge(row)}
          disabled={isRetryDisabled(row) || isCharging}
          title={retryDisabledReason || 'Reintentar cobro'}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isCharging ? 'animate-spin' : ''}`} />
          Cobrar
        </Button>
        <VirtualPosPaymentDetailDialog
          trigger={(
            <Button variant="ghost" size="xl">
              <Eye className="h-4 w-4" />
            </Button>
          )}
          headerRight={(
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRefreshChargeMetadata(row)}
                disabled={isRefreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRetryCharge(row)}
                disabled={isRetryDisabled(row) || isCharging}
                title={retryDisabledReason || 'Reintentar cobro'}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isCharging ? 'animate-spin' : ''}`} />
                Cobrar
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleOpenChargePage(row)}>
                Ver página
              </Button>
            </div>
          )}
          summary={payload.summary}
          identifiers={payload.identifiers}
          customerItems={payload.customerItems}
          collectionItems={payload.collectionItems}
          chargeItems={payload.chargeItems}
          orderItems={payload.orderItems}
          deposits={payload.deposits}
          metadataText={payload.metadataText}
          metadataExtraItems={payload.metadataExtraItems}
        />
      </div>
    );
  };

  const renderMainCells = (row) => {
    const { suscripcion, contrataciones, certificados } = getLinkedRecords(row);
    const { name, rut } = getSubscriberInfo(row);

    const dedupe = (values) => Array.from(new Set(values.filter((value) => value !== null && value !== undefined && value !== '')));

    const productos = dedupe([
      ...contrataciones.map((item) => item?.producto_nombre),
      ...certificados.map((item) => item?.producto_nombre),
    ]);
    const planesFromLinked = dedupe([
      ...contrataciones.map((item) => item?.plan_nombre),
      ...certificados.map((item) => item?.plan_nombre),
    ]);
    const planes = planesFromLinked.length > 0 ? planesFromLinked : dedupe([row.plan_name]);
    const certificadoCodigos = dedupe([
      ...certificados.map((item) => item?.codigo),
      suscripcion?.certificado_id,
    ]);

    const renderListCell = (items, dialogTitle) => {
      if (items.length === 0) return <TableCell>-</TableCell>;
      if (items.length === 1) return <TableCell>{items[0]}</TableCell>;
      return (
        <TableCell>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setItemsDialog({ title: dialogTitle, items })}
          >
            {items.length} {items.length === 1 ? 'elemento' : 'elementos'}
          </Button>
        </TableCell>
      );
    };

    return (
      <>
        <TableCell>
          <div className="flex flex-col">
            <span className="font-medium">{suscripcion?.codigo || '-'}</span>
            <span className="text-xs text-gray-600">{suscripcion?.external_subscription_id || ''}</span>
          </div>
        </TableCell>
        <TableCell>{name || '-'}</TableCell>
        <TableCell>{rut || '-'}</TableCell>
        {renderListCell(productos, 'Productos')}
        {renderListCell(planes, 'Planes')}
        {renderListCell(certificadoCodigos, 'Certificados')}
      </>
    );
  };

  const renderLatestChargeCell = (group) => {
    const latestCharge = getLatestRelevantCharge(group?.rows || []);
    if (!latestCharge) {
      return <TableCell>-</TableCell>;
    }
    const dateValue = latestCharge?.charge_date || latestCharge?.created_at;
    return (
      <TableCell>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-gray-600">{formatDate(dateValue)}</span>
          {getChargeStatusBadge(latestCharge?.status)}
        </div>
      </TableCell>
    );
  };

  const renderQuotaRow = (row) => {
    const paymentMetadata = getMetadataObject(row.payment_metadata);
    const providerMetadata = getMetadataObject(row.provider_metadata);
    const metadata = row.payment_metadata ? paymentMetadata : providerMetadata;
    const tracking = getPaymentTrackingFromMetadata(metadata);
    const value = row.processed_at || tracking.paidAt;

    return (
      <TableRow key={`quota-${row.id || row.external_charge_id || row.charge_date}`}>
        <TableCell>{getQuotaDescription(row)}</TableCell>
        <TableCell>{formatDate(row.processed_at || row.charge_date || row.created_at)}</TableCell>
        <TableCell className="font-medium">{formatChargeAmount(row.amount, row.currency)}</TableCell>
        <TableCell>{getChargeStatusBadge(row.status)}</TableCell>
        <TableCell>{formatCLP(tracking.chargedCLP)}</TableCell>
        <TableCell>{formatCLP(tracking.netCLP)}</TableCell>
        <TableCell>{formatCLP(tracking.feesCLP)}</TableCell>
        <TableCell>{tracking.depositDate ? formatDateTime(tracking.depositDate) : '-'}</TableCell>
        <TableCell>{value ? formatDateTime(value) : '-'}</TableCell>
        <TableCell className="text-right">{renderChargeDetailCell(row)}</TableCell>
      </TableRow>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Cargos de Suscripción</h1>
          <p className="text-muted-foreground">
            Revisa cargos, estados y seguimiento de cobros
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Buscar por nombre o RUT..."
            className="w-64"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filtrar estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending">Pendiente</SelectItem>
              <SelectItem value="processing">Procesando</SelectItem>
              <SelectItem value="paid">Pagado</SelectItem>
              <SelectItem value="rejected">Rechazado</SelectItem>
              <SelectItem value="cancelled">Cancelado</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={loadCharges} variant="outline" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>
      {loadError && (
        <Alert variant="destructive">
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
      )}
      {actionFeedback && (
        <Alert variant={actionFeedback.type === 'error' ? 'destructive' : 'default'}>
          <AlertDescription>{actionFeedback.message}</AlertDescription>
        </Alert>
      )}
      <Card>
        <CardHeader>
          <CardTitle>Resumen</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-7">
            <div className="flex flex-col">
              <span className="text-xs text-gray-600">Total</span>
              <span className="text-lg font-semibold">{chargeSummary.total}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-gray-600">Pendientes</span>
              <span className="text-lg font-semibold">{chargeSummary.pending}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-gray-600">Procesando</span>
              <span className="text-lg font-semibold">{chargeSummary.processing}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-gray-600">Pagadas</span>
              <span className="text-lg font-semibold">{chargeSummary.paid}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-gray-600">Rechazadas</span>
              <span className="text-lg font-semibold">{chargeSummary.rejected}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-gray-600">Canceladas</span>
              <span className="text-lg font-semibold">{chargeSummary.cancelled}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-gray-600">Cuotas vencidas</span>
              <span className="text-lg font-semibold">{chargeSummary.overdue}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Listado de Cargos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[48px]"></TableHead>
                    <TableHead>Suscripción</TableHead>
                    <TableHead>Suscriptor</TableHead>
                    <TableHead>RUT</TableHead>
                    <TableHead>Producto</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Certificado</TableHead>
                    <TableHead>Última cuota (hasta hoy)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupedCharges.length ? (
                    groupedCharges.map((group) => {
                      const isExpanded = Boolean(expandedGroups[group.key]);
                      const mainRow = group.rows[0];
                      return (
                        <React.Fragment key={group.key}>
                          <TableRow>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => toggleGroup(group.key)}
                              >
                                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </Button>
                            </TableCell>
                            {renderMainCells(mainRow)}
                            {renderLatestChargeCell(group)}
                          </TableRow>
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={8}>
                                <div className="rounded-md border bg-muted/30 p-3">
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Descripción</TableHead>
                                        <TableHead>Fecha Cobro</TableHead>
                                        <TableHead>Monto</TableHead>
                                        <TableHead>Estado</TableHead>
                                        <TableHead>Cobrado (CLP)</TableHead>
                                        <TableHead>Recaudado (CLP)</TableHead>
                                        <TableHead>Fees/Comisión</TableHead>
                                        <TableHead>Depósito</TableHead>
                                        <TableHead>Procesado</TableHead>
                                        <TableHead className="text-right">Detalle</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {group.rows.map((row) => renderQuotaRow(row))}
                                    </TableBody>
                                  </Table>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="py-0">
                        <EmptyState
                          icon={<Receipt />}
                          title="Sin cobros"
                          description="No hay cobros que coincidan con los filtros seleccionados."
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
        </CardContent>
      </Card>

      <Dialog open={Boolean(itemsDialog)} onOpenChange={(open) => { if (!open) setItemsDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{itemsDialog?.title}</DialogTitle>
          </DialogHeader>
          <ul className="space-y-1">
            {(itemsDialog?.items || []).map((item, index) => (
              <li key={`${item}-${index}`} className="text-sm border-b last:border-b-0 py-1.5">
                {item}
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const SubscriptionChargeDetailPage = () => {
  const supabase = useSupabaseClient();
  const { id } = useParams();
  const navigate = useNavigate();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chargingChargeId, setChargingChargeId] = useState(null);
  const [refreshingChargeId, setRefreshingChargeId] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [actionFeedback, setActionFeedback] = useState(null);

  const loadCharge = async () => {
    if (!id) {
      setRow(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setLoadError(null);
      // `id` en la URL puede ser el uuid interno o el identificador del PSP; payment
      // prueba los dos y devuelve null si no hay fila (ver services/yoolpay/chargesApi.js).
      setRow(await getCharge(String(id)));
    } catch (error) {
      console.error('Error loading charge detail:', error);
      setLoadError('Error al cargar el detalle del cargo.');
      setRow(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCharge();
  }, [id]);

  // Mismo criterio que en la lista: las columnas promovidas viajan en
  // `updatePayload` y se reflejan sin recargar.
  const applyChargeMetadataUpdate = (dbChargeId, newMetadata, derivedStatus, updatePayload) => {
    if (!dbChargeId || !newMetadata) return;
    setRow((prev) => {
      if (!prev) return prev;
      if (String(prev?.id || '') !== String(dbChargeId)) return prev;
      return {
        ...prev,
        payment_metadata: newMetadata,
        ...(derivedStatus ? { status: derivedStatus } : {}),
        ...(updatePayload?.amount_paid !== undefined
          ? { amount_paid: updatePayload.amount_paid }
          : {}),
        ...(updatePayload?.processed_at
          ? { processed_at: updatePayload.processed_at }
          : {})
      };
    });
  };

  const handleRetryCharge = async () => {
    const externalChargeId = row?.external_charge_id ? String(row.external_charge_id) : '';
    const dbChargeId = row?.id ? String(row.id) : '';
    if (!externalChargeId || !dbChargeId) {
      setActionFeedback({ type: 'error', message: 'No se puede cobrar: falta External Charge ID o ID interno.' });
      return;
    }
    try {
      setChargingChargeId(externalChargeId);
      setActionFeedback(null);
      await yoolpayRetryCharge(dbChargeId);
      const { metadata: newMetadata, derivedStatus, updatePayload } = await refreshChargeMetadataShared({
        getChargeById: virtualPosService.getChargeById.bind(virtualPosService),
        getPaymentById: virtualPosService.getPaymentById.bind(virtualPosService),
        supabase,
        dbChargeId,
        externalChargeId,
        existingMetadata: row?.payment_metadata || null,
        // Para no pisar un `processed_at` ya registrado al promover el cobro.
        existingCharge: row || null,
      });
      applyChargeMetadataUpdate(dbChargeId, newMetadata, derivedStatus, updatePayload);
      setActionFeedback({ type: 'success', message: 'Cobro reintentado y metadata actualizada.' });
    } catch (error) {
      console.error('Error reintentando cobro:', error);
      setActionFeedback({ type: 'error', message: error?.message || 'Error al reintentar el cobro.' });
    } finally {
      setChargingChargeId(null);
    }
  };

  const handleRefreshChargeMetadata = async () => {
    const externalChargeId = row?.external_charge_id ? String(row.external_charge_id) : '';
    const dbChargeId = row?.id ? String(row.id) : '';
    if (!externalChargeId || !dbChargeId) {
      setActionFeedback({ type: 'error', message: 'No se puede actualizar: falta External Charge ID o ID interno.' });
      return;
    }
    try {
      setRefreshingChargeId(externalChargeId);
      setActionFeedback(null);
      const { metadata: newMetadata, derivedStatus, updatePayload } = await refreshChargeMetadataShared({
        getChargeById: virtualPosService.getChargeById.bind(virtualPosService),
        getPaymentById: virtualPosService.getPaymentById.bind(virtualPosService),
        supabase,
        dbChargeId,
        externalChargeId,
        existingMetadata: row?.payment_metadata || null,
        // Para no pisar un `processed_at` ya registrado al promover el cobro.
        existingCharge: row || null,
      });
      applyChargeMetadataUpdate(dbChargeId, newMetadata, derivedStatus, updatePayload);
      setActionFeedback({ type: 'success', message: 'Metadata del cargo actualizada.' });
    } catch (error) {
      console.error('Error actualizando metadata del cargo:', error);
      setActionFeedback({ type: 'error', message: error?.message || 'Error al actualizar la metadata del cargo.' });
    } finally {
      setRefreshingChargeId(null);
    }
  };

  const payload = row ? buildChargeDetailPayload(row) : null;
  const isCharging = chargingChargeId === String(row?.external_charge_id || '');
  const isRefreshing = refreshingChargeId === String(row?.external_charge_id || '');
  const retryDisabled = isRetryDisabled(row);
  const retryDisabledReason = getRetryDisabledReason(row);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Detalle del Cargo</h1>
          <p className="text-muted-foreground">Información completa del cobro</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/payments-admin/cargos')}>
            Volver
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefreshChargeMetadata} disabled={isRefreshing || !row}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRetryCharge}
            disabled={retryDisabled || isCharging || !row}
            title={retryDisabledReason || 'Reintentar cobro'}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isCharging ? 'animate-spin' : ''}`} />
            Cobrar
          </Button>
        </div>
      </div>
      {loadError && (
        <Alert variant="destructive">
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
      )}
      {actionFeedback && (
        <Alert variant={actionFeedback.type === 'error' ? 'destructive' : 'default'}>
          <AlertDescription>{actionFeedback.message}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">Cargando detalle...</CardContent>
        </Card>
      ) : row && payload ? (
        <Card>
          <CardContent className="pt-6">
            <VirtualPosPaymentDetailContent
              summary={payload.summary}
              identifiers={payload.identifiers}
              customerItems={payload.customerItems}
              collectionItems={payload.collectionItems}
              chargeItems={payload.chargeItems}
              orderItems={payload.orderItems}
              deposits={payload.deposits}
              metadataText={payload.metadataText}
              metadataExtraItems={payload.metadataExtraItems}
            />
          </CardContent>
        </Card>
      ) : (
        <EmptyState
          icon={<Receipt />}
          title="Sin información de cobros"
          description="No se encontraron datos de cobros para esta suscripción."
        />
      )}
    </div>
  );
};

export default SubscriptionChargesList;
